import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "~/components/forms/SelectInput";
import BetterNumberInput from "~/components/forms/BetterNumberInput";
import invariant from "tiny-invariant";
import Label from "~/components/Label";
import TextInput from "~/components/forms/TextInput";

/**
 * TODO Implement generate results permalink widget with coping to clipboard
 */
export enum NumeralSystem {
  decimal = "decimal",
  binary = "binary",
  octal = "octal",
  hexadecimal = "hexadecimal",
  // roman = "roman",
}

interface Props {
  from: NumeralSystem,
  to: NumeralSystem,
  value: string,
  onChange?: (event: StateChangeEvent) => void,
}

interface StateChangeEvent {
  numberA: string,
  systemA: NumeralSystem,
  numberB: string,
  systemB: NumeralSystem
}

const NumeralSystemsConverter: React.FC<Props> = (
  {
    from,
    to,
    value = '1',
    onChange = () => {}
  }
) => {
  const { t } = useTranslation("translations");
  const TKEY: string = "calculators.numeral-systems";

  const [systemA, setSystemA] = useState<NumeralSystem>(from);
  const [systemB, setSystemB] = useState<NumeralSystem>(to);

  interface State {
    a: string,
    b: string,
  }
  const [state, setState] = useState<State>({a: value, b: ''})

  useEffect(() => {
    calculate(value, null);
  }, []);

  useEffect(() => {
    console.debug("System changed. Number A", state.a)
    calculate(state.a, null);
  }, [systemA, systemB]);


  const systemSelectorOptions = Object.values(NumeralSystem).map(system => ({
    value: system,
    name: t(`${TKEY}.systems.${system}`)
  }));

  function calculate(a: string | null, b: string | null): void {
    if ((a === null && b === null) || (a !== null && b !== null)) {
      throw Error("Must provider either A or B, not both");
    }


    const reverse = a === null;

    console.debug("Recalculating: ", a, b, systemA, systemB, reverse);

    if (reverse) {
      invariant(b !== null);
      if (b === "" || !getPatternForNumeralSystem(systemB).test(b)) {
        setState({a: "", b: ""});
        return;
      }

      a = parseInt(b, radix(systemB)).toString(radix(systemA)).toUpperCase();
      console.debug("Calculated A: ", a);
      setState({a, b});
    } else {
      invariant(a !== null);
      if (a === "" || !getPatternForNumeralSystem(systemA).test(a)) {
        setState({a: "", b: ""});
        return;
      }
      b = parseInt(a, radix(systemA)).toString(radix(systemB)).toUpperCase();
      console.log("Calculated B: ", b);

      setState({a, b});
    }

    onChange({ numberA: state.a, numberB: state.b,  systemA, systemB });
  }

  function radix(numeralSystem: NumeralSystem): number {
    switch (numeralSystem) {
      case NumeralSystem.binary:
        return 2;
      case NumeralSystem.decimal:
        return 10;
      case NumeralSystem.octal:
        return 8;
      case NumeralSystem.hexadecimal:
        return 16;
      default: throw new Error("Invalid numeral system");
    }
  }

  function getPatternForNumeralSystem(numeralSystem: NumeralSystem): RegExp {
    switch (numeralSystem) {
      case NumeralSystem.binary:
        return /^[01]+$/;
      case NumeralSystem.octal:
        return /^[0-7]+$/;
      case NumeralSystem.decimal:
        return /^\d+$/;
      case NumeralSystem.hexadecimal:
        return /^[0-9a-fA-F]+$/;
      default:
        throw new Error("Invalid numeral system");
    }
  }

  return (
    <div className="flex flex-col md:flex-row md:gap-6 items-center">
      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="w-full md:max-w-[200px] lg:max-w-[220px]">
          <Label htmlFor="numberA" text={t(`${TKEY}.from`)} />
          { systemA === NumeralSystem.decimal ? (
            <BetterNumberInput
              name="numberA"
              value={parseInt(state.a, 10)}
              onChange={(value) => value !== state.a && calculate(value.toString(), null)}
              min={0}
              maxFractionDigits={0}
              ariaLabel={t(`${TKEY}.from`)}
              autoFocus={true}
            />
          ) : (
            <TextInput
              type="text"
              name="numberA"
              value={state.a}
              onChange={(value: string) => value !== state.a && calculate(value, null)}
              ariaLabel={t(`${TKEY}.from`)}
              autoFocus={true}
              isInvalid={true}
              pattern={getPatternForNumeralSystem(systemA).source}
            />
          )}
        </div>
        <div className="w-full lg:max-w-[240px]">
          <Label htmlFor={"systemA"} text={t(`${TKEY}.system`)} />
          <SelectInput
            name="systemA"
            options={systemSelectorOptions}
            value={systemSelectorOptions.find((system) => system.value === systemA)?.value}
            ariaLabel={t(`${TKEY}.system`)}
            onChange={(value) => setSystemA(value as NumeralSystem)}
          />
        </div>
      </div>
      <div className="text-6xl pt-4 text-slate-500 dark:text-slate-400">
        <span className="hidden md:inline">
          ⬌
        </span>
        <span className="md:hidden">
          ⬍
        </span>
      </div>
      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="w-full md:max-w-[200px] lg:max-w-[220px]">
          <Label htmlFor="numberB" text={t(`${TKEY}.to`)} />
          { systemB === NumeralSystem.decimal ? (
            <BetterNumberInput
              name="numberB"
              value={parseInt(state.b, 10)}
              onChange={(value) => value !== state.b && calculate(null, value.toString())}
              min={0}
              maxFractionDigits={0}
              ariaLabel={t(`${TKEY}.to`)}
              autoFocus={true}
            />
          ) : (
            <TextInput
              type="text"
              name="numberB"
              value={state.b}
              onChange={(value: string) => value !== state.b && calculate(null, value.toString())}
              ariaLabel={t(`${TKEY}.to`)}
              pattern={getPatternForNumeralSystem(systemB).source}
            />
          )}
        </div>
        <div className="w-full lg:max-w-[240px]">
          <Label htmlFor={"systemB"} text={t(`${TKEY}.system`)} />
          <SelectInput
            name="systemB"
            options={systemSelectorOptions}
            value={systemSelectorOptions.find((system) => system.value === systemB)?.value}
            ariaLabel={t(`${TKEY}.system`)}
            onChange={(value) => setSystemB(value as NumeralSystem)}
          />
        </div>
      </div>
    </div>
  );
};

export default NumeralSystemsConverter;